import axios from 'axios';

import {
  AccessRequestToAnswerDto,
  OwnAccessRequestDto,
  TeamDto,
  TeamSubject,
} from '../dtos/property-team.dto.interface';

/** A unit's team routes, or with `building` a building's, which mirror them under its own prefix */
class PropertyTeamService {
  constructor(private readonly kind: TeamSubject['kind'] = 'property') {}

  async getTeam(id: string): Promise<TeamDto> {
    return (await axios.get<TeamDto>(`${this.kind}/${id}/team`)).data;
  }

  /** Files it into a portfolio, or out of every portfolio with `null`; its manager and collaborators stay */
  async moveToPortfolio(id: string, portfolioId: string | null): Promise<TeamDto> {
    return (await axios.patch<TeamDto>(`${this.kind}/${id}/portfolio`, { portfolioId })).data;
  }

  /** The unit's notifications and calendar events go to the new manager from now on */
  async handOff(id: string, investorId: string, keepPreviousAsCollaborator: boolean): Promise<TeamDto> {
    return (await axios.patch<TeamDto>(`${this.kind}/${id}/manager`, { investorId, keepPreviousAsCollaborator })).data;
  }

  async addCollaborator(id: string, investorId: string): Promise<TeamDto> {
    return (await axios.post<TeamDto>(`${this.kind}/${id}/collaborators`, { investorId })).data;
  }

  /** This unit or building only; they stay on the portfolio's others */
  async removeCollaborator(id: string, investorId: string): Promise<TeamDto> {
    return (await axios.delete<TeamDto>(`${this.kind}/${id}/collaborators/${investorId}`)).data;
  }

  /** Asks to work on it: its manager is emailed. Asking again answers with the request already open */
  async requestAccess(id: string): Promise<OwnAccessRequestDto> {
    return (await axios.post<OwnAccessRequestDto>(`${this.kind}/${id}/access-requests`)).data;
  }

  /** Requests the caller may answer, across the company's units: every one for an admin */
  async accessRequestsToAnswer(): Promise<AccessRequestToAnswerDto[]> {
    return (await axios.get<AccessRequestToAnswerDto[]>('company/access-requests')).data;
  }

  async declineAccessRequest(id: string, requestId: string): Promise<TeamDto> {
    return (await axios.delete<TeamDto>(`${this.kind}/${id}/access-requests/${requestId}`)).data;
  }
}

const propertyTeamService = new PropertyTeamService();
const buildingTeamService = new PropertyTeamService('building');

export function teamServiceFor(subject: TeamSubject): PropertyTeamService {
  return subject.kind === 'building' ? buildingTeamService : propertyTeamService;
}

export default propertyTeamService;
